import React, {type ReactNode} from 'react';
import {useRadioGroup, useRadio} from '@react-aria/radio';
import {useRadioGroupState} from '@react-stately/radio';
import {useHover} from '@react-aria/interactions';
import {FocusRing} from '@react-aria/focus';
import classNames from 'classnames';
import {VisuallyHidden} from '@react-aria/visually-hidden';

import {TypographySize, useTypography} from '../../../hooks/typography';
import {CircleCheckFilledIcon} from '../../../icons';

import type {Props} from './types';
import {RadioContext, useRadioProvider} from './context';
import styles from './styles.module.css';

const IconWrapper: React.FunctionComponent<{ children: ReactNode }> = ({children}) => (
    <div className={styles.iconWrapper}>{children}</div>
);

const SelectableCardContent: React.FunctionComponent<{ children: ReactNode }> = ({children}) => (
    <div className={styles.cardContent}>{children}</div>
);

const Checkmark: React.FunctionComponent<unknown> = () => (
    <div className={styles.checkCircleIconWrapper}>
        <CircleCheckFilledIcon />
    </div>
);

const Title: React.FunctionComponent<{ children: ReactNode }> = ({children}) => {
    const titleTypographyCls = useTypography({
        size: TypographySize.BodyLTrue,
    });

    return <h5 className={classNames(titleTypographyCls, styles.title)}>{children}</h5>;
};

const Description: React.FunctionComponent<{ children: ReactNode }> = ({children}) => {
    const descriptionTypographyCls = useTypography({
        size: TypographySize.BodyS,
        noDefaultColor: true,
    });

    return <span className={classNames(descriptionTypographyCls, styles.description)}>{children}</span>;
};

const SelectableCard: React.FunctionComponent<Props['options'][number]> = (props) => {
    const {autoFocus, icon, label, description, multiline} = props;
    const {hoverProps, isHovered} = useHover({isDisabled: props.isDisabled});

    const inputRef = React.useRef<HTMLInputElement>(null);

    const radioGroupProps = useRadioProvider();
    const {state} = radioGroupProps;

    const {inputProps, isSelected, isDisabled} = useRadio(
        {
            ...props,
            ...radioGroupProps,
            isDisabled: props.isDisabled,
            value: props.value as string,
        },
        state,
        inputRef,
    );

    return (
        <FocusRing focusClass={styles.isFocused} within={true} autoFocus={autoFocus}>
            <label
                {...hoverProps}
                className={classNames(
                    styles.card,
                    isSelected && styles.isSelected,
                    isHovered && styles.isHovered,
                    isDisabled && styles.isDisabled,
                    multiline && styles.multiline,
                )}
            >
                <VisuallyHidden>
                    <input {...inputProps} ref={inputRef} />
                </VisuallyHidden>
                {icon ? <IconWrapper>{icon}</IconWrapper> : null}
                <SelectableCardContent>
                    {label ? <Title>{label}</Title> : null}
                    {description ? <Description>{description}</Description> : null}
                </SelectableCardContent>
                {isSelected ? <Checkmark /> : null}
            </label>
        </FocusRing>
    );
};

export const RadioCards: React.FunctionComponent<Props> = (props) => {
    const {options, validationState, orientation = 'horizontal', multiline} = props;

    const state = useRadioGroupState(props);
    const {radioGroupProps} = useRadioGroup(props, state);

    return (
        <div className={classNames(styles.wrapper, styles[orientation])} {...radioGroupProps}>
            <RadioContext.Provider
                value={{
                    validationState,
                    state,
                }}
            >
                {options.map((option) => {
                    return <SelectableCard key={option.value} {...option} multiline={multiline} />;
                })}
            </RadioContext.Provider>
        </div>
    );
};
